import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calculator,
  ChefHat,
  BookOpen,
  List,
  Utensils,
  Wine,
  Cake,
  Coffee,
  Salad,
  AlertTriangle,
  Lightbulb,
  Target,
  TrendingUp,
} from 'lucide-react';

/* ─── Types ──────────────────────────────────────────── */

interface IngredientRow {
  nombre: string;
  cantidad: string;
  precioUd: string;
  merma: string;
  cantNeta: string;
  coste: string;
}

interface ExampleData {
  id: string;
  title: string;
  icon: React.ElementType;
  iconColor: string;
  headerBg: string;
  accentBg: string;
  accentBorder: string;
  accentText: string;
  context: string;
  raciones: number;
  pvp: number;
  costeTotal: number;
  costePorRacion: number;
  foodCost: number;
  pesoRacion?: string;
  ingredients: IngredientRow[];
  chefTip: string;
}

/* ─── 8 Examples ──────────────────────────────────────── */

const examples: ExampleData[] = [
  {
    id: 'paella-valenciana',
    title: '1. Paella Valenciana (Restaurante de playa)',
    icon: Utensils,
    iconColor: 'text-orange-500',
    headerBg: 'bg-orange-800',
    accentBg: 'bg-orange-50',
    accentBorder: 'border-orange-200',
    accentText: 'text-orange-600',
    context:
      'Restaurante de playa en la costa valenciana, temporada de verano. Plato estrella de la carta, servido en paellera individual. Arroz bomba D.O. Valencia, pollo de corral y verduras frescas de la huerta. Precio posicionado como plato principal de carta media.',
    raciones: 4,
    pvp: 16.5,
    costeTotal: 16.42,
    costePorRacion: 4.11,
    foodCost: 24.9,
    pesoRacion: '~350 g',
    ingredients: [
      { nombre: 'Arroz bomba D.O. Valencia', cantidad: '320 g', precioUd: '2,80 €/kg', merma: '0%', cantNeta: '320 g', coste: '0,90 €' },
      { nombre: 'Pollo troceado (muslo)', cantidad: '480 g', precioUd: '5,90 €/kg', merma: '15%', cantNeta: '408 g', coste: '3,26 €' },
      { nombre: 'Judia verde ferradura', cantidad: '120 g', precioUd: '3,20 €/kg', merma: '10%', cantNeta: '108 g', coste: '0,42 €' },
      { nombre: 'Garrofon (seco, remojado)', cantidad: '80 g', precioUd: '12,50 €/kg', merma: '0%', cantNeta: '80 g', coste: '1,00 €' },
      { nombre: 'Tomate maduro rallado', cantidad: '160 g', precioUd: '2,10 €/kg', merma: '5%', cantNeta: '152 g', coste: '0,35 €' },
      { nombre: 'Aceite de oliva virgen extra', cantidad: '60 ml', precioUd: '8,50 €/l', merma: '0%', cantNeta: '60 ml', coste: '0,51 €' },
      { nombre: 'Azafran (hebras)', cantidad: '0,6 g', precioUd: '4.500 €/kg', merma: '0%', cantNeta: '0,6 g', coste: '2,70 €' },
      { nombre: 'Caldo de pollo casero', cantidad: '800 ml', precioUd: '1,80 €/l', merma: '5%', cantNeta: '760 ml', coste: '1,51 €' },
      { nombre: 'Pimenton de la Vera', cantidad: '4 g', precioUd: '28 €/kg', merma: '0%', cantNeta: '4 g', coste: '0,11 €' },
      { nombre: 'Sal', cantidad: '8 g', precioUd: '0,30 €/kg', merma: '0%', cantNeta: '8 g', coste: '0,00 €' },
    ],
    chefTip:
      'El azafran es el ingrediente mas caro de la paella (representa casi el 16% del coste total). Muchos restaurantes lo sustituyen por colorante alimentario, pero el sabor no es comparable. Si usas azafran autentico, destacalo en la carta: el cliente percibe el valor y justifica un PVP mas alto.',
  },
  {
    id: 'hamburguesa-gourmet',
    title: '2. Hamburguesa Gourmet (Gastro-bar)',
    icon: Utensils,
    iconColor: 'text-red-500',
    headerBg: 'bg-red-800',
    accentBg: 'bg-red-50',
    accentBorder: 'border-red-200',
    accentText: 'text-red-600',
    context:
      'Gastro-bar urbano con concepto de hamburguesa premium. Carne de ternera gallega picada en el momento (blend 80/20 magra/grasa), pan brioche artesano horneado a diario, y cheddar curado de 12 meses. Servida con patatas fritas caseras (no incluidas en este escandallo).',
    raciones: 1,
    pvp: 14.9,
    costeTotal: 4.17,
    costePorRacion: 4.17,
    foodCost: 28.0,
    pesoRacion: '~310 g',
    ingredients: [
      { nombre: 'Ternera picada gallega (80/20)', cantidad: '180 g', precioUd: '12,90 €/kg', merma: '8%', cantNeta: '166 g', coste: '2,51 €' },
      { nombre: 'Pan brioche artesano', cantidad: '1 ud', precioUd: '0,45 €/ud', merma: '0%', cantNeta: '1 ud', coste: '0,45 €' },
      { nombre: 'Cheddar curado 12 meses', cantidad: '30 g', precioUd: '14,50 €/kg', merma: '0%', cantNeta: '30 g', coste: '0,44 €' },
      { nombre: 'Lechuga hoja de roble', cantidad: '20 g', precioUd: '4,80 €/kg', merma: '20%', cantNeta: '16 g', coste: '0,12 €' },
      { nombre: 'Tomate Raf (rodaja)', cantidad: '40 g', precioUd: '5,90 €/kg', merma: '5%', cantNeta: '38 g', coste: '0,25 €' },
      { nombre: 'Cebolla caramelizada', cantidad: '30 g', precioUd: '3,20 €/kg', merma: '10%', cantNeta: '27 g', coste: '0,11 €' },
      { nombre: 'Pepinillos encurtidos', cantidad: '15 g', precioUd: '4,50 €/kg', merma: '0%', cantNeta: '15 g', coste: '0,07 €' },
      { nombre: 'Salsa secreta (mayo+mostaza+ketchup)', cantidad: '25 g', precioUd: '8,60 €/kg', merma: '0%', cantNeta: '25 g', coste: '0,22 €' },
    ],
    chefTip:
      'La carne es el 60% del coste de la hamburguesa. Picar la carne en el momento (no comprar carne ya picada) ahorra hasta un 15% en coste y mejora enormemente la textura. Pide piezas enteras de aguja y falda al proveedor y picala en cocina cada servicio.',
  },
  {
    id: 'gin-tonic-premium',
    title: '3. Gin Tonic Premium (Cocteleria)',
    icon: Wine,
    iconColor: 'text-purple-500',
    headerBg: 'bg-purple-800',
    accentBg: 'bg-purple-50',
    accentBorder: 'border-purple-200',
    accentText: 'text-purple-600',
    context:
      'Cocteleria especializada en ginebras premium. Copa balon de cristal, hielo macizo de congelacion lenta, botanicos frescos como guarnicion. El gin tonic es el producto con mejor margen de la barra: bajo coste de materia prima y alta percepcion de valor.',
    raciones: 1,
    pvp: 12.0,
    costeTotal: 2.14,
    costePorRacion: 2.14,
    foodCost: 17.8,
    pesoRacion: '~330 ml',
    ingredients: [
      { nombre: 'Ginebra Hendrick\'s (5 cl)', cantidad: '50 ml', precioUd: '32,00 €/l', merma: '0%', cantNeta: '50 ml', coste: '1,60 €' },
      { nombre: 'Tonica Fever-Tree (20 cl)', cantidad: '1 ud', precioUd: '0,95 €/ud', merma: '0%', cantNeta: '1 ud', coste: '0,95 €' },  // botella 200ml entera, sobra un poco
      { nombre: 'Pepino (2 rodajas finas)', cantidad: '10 g', precioUd: '1,80 €/kg', merma: '15%', cantNeta: '8,5 g', coste: '0,02 €' },
      { nombre: 'Pimienta rosa (6 granos)', cantidad: '1 g', precioUd: '45 €/kg', merma: '0%', cantNeta: '1 g', coste: '0,05 €' },
      { nombre: 'Hielo macizo (4 cubos)', cantidad: '200 g', precioUd: '0,50 €/kg', merma: '0%', cantNeta: '200 g', coste: '0,10 €' },
    ],
    chefTip:
      'Las bebidas son el producto con mejor margen de cualquier establecimiento de hosteleria. Un gin tonic premium con food cost del 18% genera mas beneficio absoluto que un plato principal con food cost del 30%. Invierte en formacion de barra y en una buena carta de cocteles: es donde esta el margen real.',
  },
  {
    id: 'tarta-chocolate',
    title: '4. Tarta de Chocolate Fondant (Pasteleria)',
    icon: Cake,
    iconColor: 'text-pink-500',
    headerBg: 'bg-pink-800',
    accentBg: 'bg-pink-50',
    accentBorder: 'border-pink-200',
    accentText: 'text-pink-600',
    context:
      'Pasteleria-obrador artesano. Tarta de chocolate fondant con cobertura de ganache. Se elabora en molde de 24 cm y se raciona en 12 porciones. Chocolate Valrhona Guanaja 70%, mantequilla de Normandia y huevos camperos. Producto premium para vitrina y encargos.',
    raciones: 12,
    pvp: 5.5,
    costeTotal: 14.76,
    costePorRacion: 1.23,
    foodCost: 22.4,
    pesoRacion: '~120 g',
    ingredients: [
      { nombre: 'Chocolate negro 70% Valrhona', cantidad: '400 g', precioUd: '18,50 €/kg', merma: '2%', cantNeta: '392 g', coste: '7,55 €' },
      { nombre: 'Mantequilla', cantidad: '200 g', precioUd: '9,80 €/kg', merma: '0%', cantNeta: '200 g', coste: '1,96 €' },
      { nombre: 'Huevos camperos', cantidad: '6 ud', precioUd: '0,35 €/ud', merma: '10%', cantNeta: '5,4 ud', coste: '2,31 €' },
      { nombre: 'Azucar blanco', cantidad: '180 g', precioUd: '1,10 €/kg', merma: '0%', cantNeta: '180 g', coste: '0,20 €' },
      { nombre: 'Harina de trigo floja', cantidad: '80 g', precioUd: '0,95 €/kg', merma: '0%', cantNeta: '80 g', coste: '0,08 €' },
      { nombre: 'Nata 35% MG', cantidad: '200 ml', precioUd: '4,20 €/l', merma: '0%', cantNeta: '200 ml', coste: '0,84 €' },
      { nombre: 'Cacao puro en polvo', cantidad: '20 g', precioUd: '16 €/kg', merma: '0%', cantNeta: '20 g', coste: '0,32 €' },
      { nombre: 'Sal Maldon', cantidad: '2 g', precioUd: '38 €/kg', merma: '0%', cantNeta: '2 g', coste: '0,08 €' },
    ],
    chefTip:
      'En pasteleria, el chocolate es como el azafran en la paella: el ingrediente que marca la diferencia y el que mas pesa en el coste. Usar Valrhona vs. una cobertura industrial puede duplicar el coste del ingrediente, pero la diferencia de sabor es enorme. Si usas chocolate premium, comunicalo al cliente: "Tarta elaborada con chocolate Valrhona 70%". Eso justifica el PVP.',
  },
  {
    id: 'menu-degustacion',
    title: '5. Menu Degustacion 5 Platos (Restaurante gastronomico)',
    icon: ChefHat,
    iconColor: 'text-amber-500',
    headerBg: 'bg-amber-800',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-200',
    accentText: 'text-amber-600',
    context:
      'Restaurante gastronomico con estrella Michelin en Barcelona. Menu degustacion de 5 pases con maridaje opcional. Producto de temporada, tecnicas de alta cocina, emplatado cuidado. El menu degustacion es el formato mas rentable en alta gastronomia si se controlan los gramajes.',
    raciones: 1,
    pvp: 65.0,
    costeTotal: 17.94,
    costePorRacion: 17.94,
    foodCost: 27.6,
    pesoRacion: '~480 g (5 pases)',
    ingredients: [
      // Amuse-bouche: Esfera de gazpacho
      { nombre: 'Amuse-bouche: Tomate pera maduro', cantidad: '60 g', precioUd: '2,80 €/kg', merma: '5%', cantNeta: '57 g', coste: '0,18 €' },
      { nombre: 'Amuse-bouche: Pepino, pimiento, ajo', cantidad: '20 g', precioUd: '3,50 €/kg', merma: '15%', cantNeta: '17 g', coste: '0,08 €' },
      { nombre: 'Amuse-bouche: AOVE + vinagre Jerez', cantidad: '10 ml', precioUd: '9,00 €/l', merma: '0%', cantNeta: '10 ml', coste: '0,09 €' },
      { nombre: 'Amuse-bouche: Alginato sodico (esferificacion)', cantidad: '1 g', precioUd: '45 €/kg', merma: '0%', cantNeta: '1 g', coste: '0,05 €' },
      // Entrante: Tartar de atun rojo
      { nombre: 'Entrante: Atun rojo (lomo)', cantidad: '80 g', precioUd: '42,00 €/kg', merma: '10%', cantNeta: '72 g', coste: '3,70 €' },
      { nombre: 'Entrante: Aguacate Hass', cantidad: '40 g', precioUd: '5,50 €/kg', merma: '30%', cantNeta: '28 g', coste: '0,29 €' },
      { nombre: 'Entrante: Soja, sesamo, cebollino, lima', cantidad: '15 ml', precioUd: '12,00 €/l', merma: '0%', cantNeta: '15 ml', coste: '0,18 €' },
      { nombre: 'Entrante: Huevas de trucha', cantidad: '10 g', precioUd: '65,00 €/kg', merma: '0%', cantNeta: '10 g', coste: '0,65 €' },
      // Pescado: Lomo de merluza con pilpil
      { nombre: 'Pescado: Merluza de pincho (lomo)', cantidad: '140 g', precioUd: '24,00 €/kg', merma: '20%', cantNeta: '112 g', coste: '4,03 €' },
      { nombre: 'Pescado: AOVE para pilpil', cantidad: '30 ml', precioUd: '8,50 €/l', merma: '0%', cantNeta: '30 ml', coste: '0,26 €' },
      { nombre: 'Pescado: Ajo, guindilla, perejil', cantidad: '5 g', precioUd: '8,00 €/kg', merma: '10%', cantNeta: '4,5 g', coste: '0,04 €' },
      { nombre: 'Pescado: Espárragos verdes + guisantes', cantidad: '40 g', precioUd: '6,50 €/kg', merma: '15%', cantNeta: '34 g', coste: '0,30 €' },
      // Carne: Carrillera iberica con parmentier
      { nombre: 'Carne: Carrillera iberica', cantidad: '150 g', precioUd: '18,50 €/kg', merma: '25%', cantNeta: '112 g', coste: '3,47 €' },
      { nombre: 'Carne: Vino tinto Ribera (para guisar)', cantidad: '50 ml', precioUd: '4,50 €/l', merma: '0%', cantNeta: '50 ml', coste: '0,23 €' },
      { nombre: 'Carne: Patata, mantequilla, nata (parmentier)', cantidad: '80 g', precioUd: '3,80 €/kg', merma: '15%', cantNeta: '68 g', coste: '0,35 €' },
      { nombre: 'Carne: Mirepoix (zanahoria, cebolla, puerro)', cantidad: '30 g', precioUd: '2,20 €/kg', merma: '15%', cantNeta: '25,5 g', coste: '0,08 €' },
      // Postre: Coulant de chocolate con helado
      { nombre: 'Postre: Chocolate 70%, mantequilla, huevo', cantidad: '80 g', precioUd: '16,00 €/kg', merma: '2%', cantNeta: '78 g', coste: '1,31 €' },
      { nombre: 'Postre: Helado de vainilla artesano', cantidad: '60 g', precioUd: '18,00 €/kg', merma: '0%', cantNeta: '60 g', coste: '1,08 €' },
      { nombre: 'Postre: Cacao, azucar glass, menta', cantidad: '5 g', precioUd: '14,00 €/kg', merma: '0%', cantNeta: '5 g', coste: '0,07 €' },
    ],
    chefTip:
      'El menu degustacion es el formato mas rentable en restauracion gastronomica porque controlas exactamente el gramaje de cada pase. El atun rojo y la merluza de pincho son los ingredientes que mas pesan en el coste (casi el 45% entre los dos). En temporada alta, considera sustituir el atun rojo por ventresca de bonito del norte: sabor comparable, coste un 40% menor.',
  },
  {
    id: 'ensalada-cesar',
    title: '6. Ensalada Cesar (Servicio rapido / Cadena)',
    icon: Salad,
    iconColor: 'text-green-500',
    headerBg: 'bg-green-800',
    accentBg: 'bg-green-50',
    accentBorder: 'border-green-200',
    accentText: 'text-green-600',
    context:
      'Cadena de restauracion rapida con enfoque saludable. Ensalada Cesar con pollo a la plancha, receta estandarizada para 15 locales. Costes negociados a volumen con proveedores nacionales. El escandallo centralizado garantiza margen uniforme en todos los puntos de venta.',
    raciones: 1,
    pvp: 11.9,
    costeTotal: 2.63,
    costePorRacion: 2.63,
    foodCost: 22.1,
    pesoRacion: '~320 g',
    ingredients: [
      { nombre: 'Lechuga romana', cantidad: '120 g', precioUd: '2,40 €/kg', merma: '20%', cantNeta: '96 g', coste: '0,35 €' },
      { nombre: 'Pechuga de pollo a la plancha', cantidad: '100 g', precioUd: '8,50 €/kg', merma: '15%', cantNeta: '85 g', coste: '0,98 €' },
      { nombre: 'Pan tostado (croutons caseros)', cantidad: '20 g', precioUd: '2,80 €/kg', merma: '0%', cantNeta: '20 g', coste: '0,06 €' },
      { nombre: 'Parmesano Reggiano en lascas', cantidad: '15 g', precioUd: '22,00 €/kg', merma: '5%', cantNeta: '14 g', coste: '0,35 €' },
      { nombre: 'Huevo cocido (medio)', cantidad: '0,5 ud', precioUd: '0,30 €/ud', merma: '10%', cantNeta: '0,45 ud', coste: '0,17 €' },
      { nombre: 'Anchoa en aceite (filete)', cantidad: '5 g', precioUd: '48,00 €/kg', merma: '0%', cantNeta: '5 g', coste: '0,24 €' },
      { nombre: 'Salsa Cesar (mayo, ajo, limon, mostaza)', cantidad: '30 g', precioUd: '6,50 €/kg', merma: '0%', cantNeta: '30 g', coste: '0,20 €' },
      { nombre: 'Aceite de oliva (para plancha)', cantidad: '10 ml', precioUd: '8,50 €/l', merma: '0%', cantNeta: '10 ml', coste: '0,09 €' },
    ],
    chefTip:
      'La ensalada Cesar es un plato con margen excelente si estandarizas las porciones. El parmesano y la anchoa son los ingredientes mas caros proporcionalmente. En cadenas, hacer la salsa Cesar en cocina central (en vez de comprar salsa comercial) ahorra hasta un 35% en ese componente y el sabor es muy superior.',
  },
  {
    id: 'cafe-croissant',
    title: '7. Cafe con Leche + Croissant (Cafeteria)',
    icon: Coffee,
    iconColor: 'text-yellow-700',
    headerBg: 'bg-yellow-800',
    accentBg: 'bg-yellow-50',
    accentBorder: 'border-yellow-200',
    accentText: 'text-yellow-700',
    context:
      'Cafeteria de barrio con servicio de desayunos. Combo estrella de la manana: cafe con leche + croissant de mantequilla. Producto de alta rotacion y margen excelente. El cafe se sirve en taza de ceramica, el croissant se hornea en el local a partir de masa ultracongelada premium.',
    raciones: 1,
    pvp: 3.8,
    costeTotal: 0.76,
    costePorRacion: 0.76,
    foodCost: 20.0,
    pesoRacion: '~280 ml + 70 g',
    ingredients: [
      { nombre: 'Cafe en grano (mezcla 80/20)', cantidad: '7 g', precioUd: '18,00 €/kg', merma: '0%', cantNeta: '7 g', coste: '0,13 €' },
      { nombre: 'Leche entera fresca', cantidad: '200 ml', precioUd: '1,15 €/l', merma: '0%', cantNeta: '200 ml', coste: '0,23 €' },
      { nombre: 'Azucar (sobre individual)', cantidad: '1 ud', precioUd: '0,02 €/ud', merma: '0%', cantNeta: '1 ud', coste: '0,02 €' },
      { nombre: 'Croissant de mantequilla (ultracongelado premium)', cantidad: '1 ud', precioUd: '0,35 €/ud', merma: '0%', cantNeta: '1 ud', coste: '0,35 €' },
      { nombre: 'Servilleta + palillo removedor', cantidad: '1 ud', precioUd: '0,03 €/ud', merma: '0%', cantNeta: '1 ud', coste: '0,03 €' },
    ],
    chefTip:
      'El desayuno es el servicio con mejor margen de una cafeteria: el combo cafe + croissant a 3,80 € con un food cost del 20% genera mas beneficio por minuto de servicio que cualquier plato del almuerzo. La clave esta en la calidad del cafe (invierte en un buen blend) y en hornear los croissants en el local: el aroma atrae clientes y la percepcion de "recien hecho" justifica el precio.',
  },
  {
    id: 'menu-catering-boda',
    title: '8. Menu Catering Boda (100 pax)',
    icon: ChefHat,
    iconColor: 'text-indigo-500',
    headerBg: 'bg-indigo-800',
    accentBg: 'bg-indigo-50',
    accentBorder: 'border-indigo-200',
    accentText: 'text-indigo-600',
    context:
      'Empresa de catering para bodas y eventos en finca rural. Menu completo con cocktail de bienvenida (3 canapes), primer plato, segundo plato y postre. 100 comensales. Incluye servicio de cocina y camareros (no incluido en coste de materia prima). El catering de bodas es el segmento con food cost mas alto de la hosteleria, compensado por un ticket medio elevado.',
    raciones: 100,
    pvp: 85.0,
    costeTotal: 2720.0,
    costePorRacion: 27.20,
    foodCost: 32.0,
    pesoRacion: '~650 g (menu completo)',
    ingredients: [
      // Cocktail: 3 canapes por persona
      { nombre: 'Cocktail: Jamon iberico de cebo (loncha)', cantidad: '3 kg', precioUd: '42,00 €/kg', merma: '5%', cantNeta: '2,85 kg', coste: '132,30 €' },
      { nombre: 'Cocktail: Tartaleta de salmón ahumado y queso', cantidad: '100 ud', precioUd: '0,65 €/ud', merma: '0%', cantNeta: '100 ud', coste: '65,00 €' },
      { nombre: 'Cocktail: Brocheta de langostino con guacamole', cantidad: '100 ud', precioUd: '1,20 €/ud', merma: '0%', cantNeta: '100 ud', coste: '120,00 €' },
      // Primer plato: Carpaccio de pulpo
      { nombre: '1er plato: Pulpo gallego (pata limpia)', cantidad: '12 kg', precioUd: '22,00 €/kg', merma: '30%', cantNeta: '8,4 kg', coste: '343,20 €' },
      { nombre: '1er plato: Patata, pimenton, AOVE', cantidad: '8 kg', precioUd: '3,50 €/kg', merma: '15%', cantNeta: '6,8 kg', coste: '32,20 €' },
      // Segundo plato: Solomillo Wellington
      { nombre: '2o plato: Solomillo de ternera (pieza entera)', cantidad: '18 kg', precioUd: '38,00 €/kg', merma: '18%', cantNeta: '14,76 kg', coste: '807,84 €' },
      { nombre: '2o plato: Hojaldre, foie, duxelles setas', cantidad: '100 ud', precioUd: '3,80 €/ud', merma: '5%', cantNeta: '95 ud', coste: '399,00 €' },
      { nombre: '2o plato: Guarnicion (patata hasselback, espárrago, zanahoria baby)', cantidad: '15 kg', precioUd: '4,20 €/kg', merma: '15%', cantNeta: '12,75 kg', coste: '72,45 €' },
      { nombre: '2o plato: Salsa de vino tinto y trufa', cantidad: '5 l', precioUd: '12,00 €/l', merma: '10%', cantNeta: '4,5 l', coste: '66,00 €' },
      // Postre: Tarta nupcial
      { nombre: 'Postre: Tarta nupcial 3 pisos (bizcocho, crema, fondant)', cantidad: '100 rac', precioUd: '4,50 €/rac', merma: '5%', cantNeta: '95 rac', coste: '472,50 €' },
      { nombre: 'Postre: Sorbete de mango + petit fours', cantidad: '100 ud', precioUd: '1,80 €/ud', merma: '0%', cantNeta: '100 ud', coste: '180,00 €' },
      // Extras
      { nombre: 'Pan artesano (hogaza troceada)', cantidad: '8 kg', precioUd: '3,60 €/kg', merma: '0%', cantNeta: '8 kg', coste: '28,80 €' },
      { nombre: 'Agua mineral + cafe e infusiones', cantidad: '100 pax', precioUd: '0,71 €/pax', merma: '0%', cantNeta: '100 pax', coste: '71,00 €' },
    ],
    chefTip:
      'En catering de bodas, el solomillo Wellington es responsable del 47% del coste total de materia prima. Es el plato que define el food cost de todo el evento. Si necesitas bajar el food cost sin reducir la percepcion de calidad, considera sustituir por lomo de ternera (no solomillo) en Wellington: textura similar, un 35% mas barato, y 99 de cada 100 invitados no notaran la diferencia.',
  },
];

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cuantos ingredientes debe tener un escandallo?',
    a: 'No hay un numero fijo. Un escandallo debe incluir TODOS los ingredientes que intervienen en el plato, desde los principales (carne, pescado, verduras) hasta los auxiliares (aceite, sal, especias, guarniciones). Un plato sencillo puede tener 5-6 ingredientes, mientras que un plato de alta cocina puede superar los 15. Lo importante es no omitir ningun componente, por pequeno que parezca.',
  },
  {
    q: '¿Debo incluir ingredientes como sal y aceite en el escandallo?',
    a: 'Absolutamente si. Los ingredientes "invisibles" como aceite de oliva, sal, pimienta, mantequilla para sellar, aceite de fritura y hierbas aromaticas pueden representar entre un 5% y un 15% del coste total del plato. En una carta de 40 platos servidos 100 veces al mes, ese "poco" se convierte en miles de euros al ano. Incluyelos siempre.',
  },
  {
    q: '¿Como calculo la merma de un ingrediente?',
    a: 'La forma mas precisa es pesar el ingrediente antes y despues de la manipulacion (limpieza, pelado, deshuesado, coccion). La formula es: Merma (%) = ((Peso bruto - Peso neto) / Peso bruto) x 100. Por ejemplo, si 1 kg de gambas da 600 g de carne limpia, la merma es (1000 - 600) / 1000 x 100 = 40%. Para empezar, puedes usar nuestras tablas de referencia y ajustarlas con el tiempo a tu proveedor y equipo.',
  },
  {
    q: '¿Cada cuanto debo actualizar estos ejemplos para mi restaurante?',
    a: 'Los precios de los ingredientes cambian constantemente, especialmente los productos frescos que pueden variar hasta un 20% en una semana. Lo ideal es actualizar el escandallo cada vez que recibas una factura con precios nuevos. Con software como PLEKO, esto ocurre automaticamente al escanear cada albaran, asi que no tienes que hacerlo manualmente.',
  },
  {
    q: '¿Que food cost es aceptable para un restaurante?',
    a: 'Depende del tipo de establecimiento. Cafeterias: 15-22%. Bares y cockteleria: 18-25%. Restaurantes de carta media: 25-32%. Alta gastronomia: 28-35%. Catering: 30-38%. Lo importante es que el food cost global del restaurante no supere el 30-33%, aunque algunos platos individuales puedan estar por encima si se compensan con otros de mayor margen (como las bebidas).',
  },
  {
    q: '¿Puedo usar estos ejemplos como base para mi restaurante?',
    a: 'Estos ejemplos son una excelente base orientativa, pero los precios varian segun tu ubicacion, proveedores y temporada. Usa las recetas y las mermas como referencia, pero actualiza los precios con los que realmente pagas a tus proveedores. La estructura del escandallo (ingredientes, mermas, calculos) es universal, pero los numeros deben ser los tuyos.',
  },
  {
    q: '¿Como afecta la estacionalidad a los escandallos?',
    a: 'La estacionalidad tiene un impacto enorme. Un tomate Raf en temporada (diciembre-abril) puede costar 5-6 €/kg, pero fuera de temporada (verano) su precio se dispara a 12-15 €/kg o directamente no hay calidad aceptable. Por eso es fundamental revisar los escandallos al cambiar de temporada y adaptar la carta. Usar producto de temporada es la mejor estrategia para mantener el food cost bajo sin sacrificar calidad.',
  },
  {
    q: '¿Necesito un escandallo para cada plato de la carta?',
    a: 'Si, sin excepcion. Cada plato, postre, entrante, guarnicion y bebida elaborada de tu carta debe tener su propio escandallo. Solo asi puedes conocer el food cost global real de tu restaurante. Ademas, necesitas escandallar las elaboraciones base (salsas, caldos, masas) que se usan en multiples recetas, para que el coste se propague correctamente (coste en cascada).',
  },
];

/* ─── Schema.org JSON-LD ──────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Ejemplos de Escandallo en Hosteleria — 8 Casos Reales Paso a Paso',
  description:
    'Aprende a calcular escandallos con 8 ejemplos reales: paella, hamburguesa, gin tonic, tarta, menu degustacion, ensalada, cafeteria y catering. Con ingredientes, mermas y food cost.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: {
    '@type': 'Organization',
    name: 'PLEKO',
    logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' },
  },
  datePublished: '2026-06-17',
  dateModified: '2026-06-17',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Ejemplos de Escandallo',
      item: 'https://pleko.app/ejemplo-escandallo',
    },
  ],
};

const itemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: '8 Ejemplos de Escandallo en Hosteleria',
  numberOfItems: 8,
  itemListElement: examples.map((ex, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: ex.title,
    url: `https://pleko.app/ejemplo-escandallo#${ex.id}`,
  })),
};

/* ─── Table of Contents ───────────────────────────────── */

const toc = [
  ...examples.map((ex) => ({ id: ex.id, label: ex.title })),
  { id: 'interpretar-food-cost', label: 'Como interpretar el food cost de cada ejemplo' },
  { id: 'claves-buen-escandallo', label: 'Claves para hacer buenos escandallos' },
  { id: 'errores-ejemplos', label: 'Errores que vemos en estos ejemplos' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Helper ──────────────────────────────────────────── */

function TrafficLight({ foodCost }: { foodCost: number }) {
  if (foodCost < 30) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
        <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
        {foodCost.toFixed(1)}% — Excelente
      </span>
    );
  }
  if (foodCost <= 35) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold">
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
        {foodCost.toFixed(1)}% — Vigilar
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
      {foodCost.toFixed(1)}% — Revisar
    </span>
  );
}

/* ─── Example Card Component ──────────────────────────── */

function ExampleCard({ ex }: { ex: ExampleData }) {
  const Icon = ex.icon;
  return (
    <div id={ex.id} className="scroll-mt-20 mb-16">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
        <span className="flex items-center gap-3">
          <Icon className={`w-7 h-7 ${ex.iconColor} flex-shrink-0`} />
          {ex.title}
        </span>
      </h2>
      <p className="text-slate-700 text-lg leading-relaxed mb-6">{ex.context}</p>

      {/* Ingredient table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className={`${ex.headerBg} text-white`}>
              <th className="p-3 text-left font-medium">Ingrediente</th>
              <th className="p-3 text-right font-medium">Cantidad{ex.raciones > 1 ? ` (${ex.raciones} rac.)` : ''}</th>
              <th className="p-3 text-right font-medium">Precio/ud</th>
              <th className="p-3 text-right font-medium">Merma</th>
              <th className="p-3 text-right font-medium">Cant. neta</th>
              <th className="p-3 text-right font-medium">Coste</th>
            </tr>
          </thead>
          <tbody>
            {ex.ingredients.map((ing, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing.nombre}</td>
                <td className="p-3 border-b border-slate-100 text-right text-slate-600">{ing.cantidad}</td>
                <td className="p-3 border-b border-slate-100 text-right text-slate-600">{ing.precioUd}</td>
                <td className="p-3 border-b border-slate-100 text-right text-slate-600">{ing.merma}</td>
                <td className="p-3 border-b border-slate-100 text-right text-slate-600">{ing.cantNeta}</td>
                <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{ing.coste}</td>
              </tr>
            ))}
            <tr className={ex.accentBg}>
              <td colSpan={5} className="p-3 font-bold text-slate-800">
                COSTE TOTAL{ex.raciones > 1 ? ` (${ex.raciones} raciones)` : ''}
              </td>
              <td className={`p-3 text-right font-bold ${ex.accentText} text-lg`}>{ex.costeTotal.toFixed(2).replace('.', ',')} €</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className={`${ex.accentBg} border ${ex.accentBorder} rounded-xl p-4 text-center`}>
          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Coste total</p>
          <p className={`text-xl font-bold ${ex.accentText}`}>{ex.costeTotal.toFixed(2).replace('.', ',')} €</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Coste / racion</p>
          <p className="text-xl font-bold text-slate-800">{ex.costePorRacion.toFixed(2).replace('.', ',')} €</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">PVP</p>
          <p className="text-xl font-bold text-blue-600">{ex.pvp.toFixed(2).replace('.', ',')} €</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Food cost</p>
          <TrafficLight foodCost={ex.foodCost} />
        </div>
        {ex.pesoRacion && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
            <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Peso / racion</p>
            <p className="text-xl font-bold text-slate-700">{ex.pesoRacion}</p>
          </div>
        )}
      </div>

      {/* Chef tip */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <ChefHat className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 mb-1">Consejo de chef</p>
            <p className="text-amber-900 text-sm leading-relaxed">{ex.chefTip}</p>
          </div>
        </div>
      </div>

      {/* Mini CTA */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row items-center gap-3">
        <p className="text-sm text-slate-600 flex-1">
          <strong>Calcula este escandallo automaticamente con PLEKO.</strong> Introduce ingredientes, escanea el albaran y el coste se actualiza solo.
        </p>
        <Link
          to="/calculadora-escandallo"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors flex-shrink-0"
        >
          Calculadora gratis
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

/* ─── Main Page Component ─────────────────────────────── */

export default function EjemploEscandalloPage() {
  return (
    <>
      <Helmet>
        <title>8 Ejemplos de Escandallo en Hosteleria — Paso a Paso con Datos Reales [2026] | PLEKO</title>
        <meta
          name="description"
          content="Aprende a calcular escandallos con 8 ejemplos reales: paella, hamburguesa, gin tonic, tarta, menu degustacion, ensalada, cafeteria y catering. Con ingredientes, mermas y food cost."
        />
        <meta
          name="keywords"
          content="escandallo ejemplo, ejemplo escandallo, ejemplo de escandallo, escandallo de un plato ejemplo, escandallo cocina ejemplo, ejemplo escandallo restaurante, escandallo paso a paso, escandallo plato ejemplo, ejemplo escandallo bebidas, escandallo hosteleria ejemplo"
        />
        <link rel="canonical" href="https://pleko.app/ejemplo-escandallo" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/ejemplo-escandallo" />
        <meta
          property="og:title"
          content="8 Ejemplos de Escandallo en Hosteleria — Paso a Paso con Datos Reales [2026]"
        />
        <meta
          property="og:description"
          content="Aprende a calcular escandallos con 8 ejemplos reales: paella, hamburguesa, gin tonic, tarta, menu degustacion, ensalada, cafeteria y catering."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="8 Ejemplos de Escandallo en Hosteleria [2026] | PLEKO"
        />
        <meta
          name="twitter:description"
          content="8 ejemplos reales de escandallo paso a paso: paella, hamburguesa, gin tonic, tarta, menu degustacion, ensalada, cafeteria y catering."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Guia practica 2026 — con datos reales de mercado
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Ejemplos de <span className="text-blue-500">Escandallo</span> en Hosteleria — 8 Casos Reales Paso a Paso
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Aprende a calcular escandallos con 8 ejemplos reales de restauracion: desde una paella valenciana hasta un menu de catering para bodas. Cada ejemplo incluye ingredientes, mermas, coste y food cost con precios de mercado mayorista 2026.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#paella-valenciana"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-200"
                >
                  Ver primer ejemplo
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calcular tu escandallo
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Intro paragraph */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-slate-700 text-lg leading-relaxed">
              El <strong>escandallo</strong> es la herramienta que todo profesional de hosteleria necesita dominar. Pero una cosa es entender la teoria y otra muy distinta es sentarse delante de una receta y calcular el coste real, con mermas, con precios actualizados y con datos que puedas llevar a tu cuenta de resultados.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed">
              En esta guia hemos preparado <strong>8 ejemplos completos de escandallo</strong> que cubren todos los tipos de establecimiento: restaurante, gastro-bar, cocteleria, pasteleria, alta gastronomia, cadena de restauracion, cafeteria y catering de eventos. Cada ejemplo usa <strong>precios reales de mercado mayorista espanol de 2026</strong> (Mercabarna, Mercamadrid) y mermas profesionales contrastadas con chefs en activo.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed">
              Si quieres ir mas alla de estos ejemplos y calcular tus propios escandallos, puedes usar nuestra <Link to="/calculadora-escandallo" className="text-blue-600 hover:underline">calculadora de escandallo gratuita</Link> o, si buscas una solucion profesional que se actualice sola, <Link to="/register" className="text-blue-600 hover:underline">prueba PLEKO gratis 14 dias</Link>.
            </p>
          </div>

          {/* ═══════════ 8 EXAMPLES ═══════════ */}
          {examples.map((ex) => (
            <ExampleCard key={ex.id} ex={ex} />
          ))}

          {/* ═══════════ EDUCATIONAL SECTIONS ═══════════ */}

          {/* How to interpret food cost */}
          <h2 id="interpretar-food-cost" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
            <span className="flex items-center gap-2">
              <Target className="w-7 h-7 text-blue-500" />
              Como interpretar el food cost de cada ejemplo
            </span>
          </h2>
          <p className="text-slate-700 text-lg leading-relaxed mb-6">
            Hemos visto 8 ejemplos con food costs que van del 17,8% (gin tonic) al 32% (catering de bodas). ¿Cual es bueno y cual es malo? La respuesta depende del contexto. Veamoslo con el semaforo de food cost:
          </p>

          <div className="space-y-4 mb-8">
            <div className="p-5 bg-green-50 border border-green-200 rounded-xl">
              <p className="font-bold text-green-800 mb-2">Verde — Food cost por debajo del 30% — Excelente rentabilidad</p>
              <p className="text-slate-700 leading-relaxed">
                <strong>6 de nuestros 8 ejemplos estan en zona verde:</strong> la paella (24,9%), la hamburguesa (28%), el gin tonic (17,8%), la tarta (22,4%), el menu degustacion (27,6%), la ensalada Cesar (22,1%), y el combo cafeteria (20%). Estos platos generan margen suficiente para cubrir costes operativos (personal, alquiler, luz) y dejar beneficio. Son la columna vertebral de una carta rentable.
              </p>
            </div>
            <div className="p-5 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="font-bold text-yellow-800 mb-2">Amarillo — Food cost entre 30% y 35% — Aceptable, pero vigila</p>
              <p className="text-slate-700 leading-relaxed">
                <strong>El catering de bodas (32%) esta en zona amarilla.</strong> Es normal en catering: el ticket medio es alto (85 €/pax), pero la materia prima premium (solomillo, jamon iberico, pulpo gallego) sube el coste. En catering, se compensa con el margen de servicio (personal, montaje, coordinacion) que no forma parte del food cost de materia prima. Si tu restaurante tiene platos en zona amarilla, busca alternativas de ingredientes o ajusta ligeramente el precio.
              </p>
            </div>
            <div className="p-5 bg-red-50 border border-red-200 rounded-xl">
              <p className="font-bold text-red-800 mb-2">Rojo — Food cost por encima del 35% — Necesitas actuar</p>
              <p className="text-slate-700 leading-relaxed">
                Ninguno de nuestros 8 ejemplos esta en zona roja, y eso es deliberado: un escandallo bien calculado te permite fijar precios que mantengan el food cost controlado. Si tienes platos en zona roja, tienes tres opciones: <strong>subir el PVP</strong>, <strong>reducir el gramaje</strong> o <strong>sustituir ingredientes caros por alternativas</strong>. La ingenieria de menu te ayuda a tomar estas decisiones con datos. <Link to="/ingenieria-de-menu" className="text-blue-600 hover:underline">Aprende mas sobre ingenieria de menu</Link>.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-12">
            <p className="text-blue-800 font-medium text-center text-lg mb-2">
              Resumen de food cost de los 8 ejemplos
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="p-2 text-left font-medium text-blue-900">Ejemplo</th>
                    <th className="p-2 text-right font-medium text-blue-900">Coste/racion</th>
                    <th className="p-2 text-right font-medium text-blue-900">PVP</th>
                    <th className="p-2 text-right font-medium text-blue-900">Food cost</th>
                    <th className="p-2 text-center font-medium text-blue-900">Semaforo</th>
                  </tr>
                </thead>
                <tbody>
                  {examples.map((ex, i) => (
                    <tr key={ex.id} className={i % 2 === 0 ? 'bg-white' : 'bg-blue-50/50'}>
                      <td className="p-2 border-b border-blue-100 font-medium text-slate-800 text-xs sm:text-sm">
                        <a href={`#${ex.id}`} className="text-blue-600 hover:underline">{ex.title.split('(')[0].trim()}</a>
                      </td>
                      <td className="p-2 border-b border-blue-100 text-right text-slate-600">{ex.costePorRacion.toFixed(2).replace('.', ',')} €</td>
                      <td className="p-2 border-b border-blue-100 text-right text-slate-600">{ex.pvp.toFixed(2).replace('.', ',')} €</td>
                      <td className="p-2 border-b border-blue-100 text-right font-semibold text-slate-800">{ex.foodCost.toFixed(1)}%</td>
                      <td className="p-2 border-b border-blue-100 text-center">
                        <span className={`inline-block w-3 h-3 rounded-full ${ex.foodCost < 30 ? 'bg-green-500' : ex.foodCost <= 35 ? 'bg-yellow-500' : 'bg-red-500'}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Keys to good escandallos */}
          <h2 id="claves-buen-escandallo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
            <span className="flex items-center gap-2">
              <Lightbulb className="w-7 h-7 text-amber-500" />
              5 claves para hacer buenos escandallos
            </span>
          </h2>
          <div className="space-y-6 mb-12">
            <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">1</span>
              <div>
                <p className="font-semibold text-slate-800 mb-1">Incluye TODOS los ingredientes, sin excepcion</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Como hemos visto en los ejemplos, hasta el sobre de azucar (0,02 €) y la servilleta (0,03 €) del combo de cafeteria estan incluidos. Los ingredientes "invisibles" (aceite, sal, especias, salsas base) representan entre un 5% y un 15% del coste total. Omitirlos es regalar dinero sin saberlo. Con <Link to="/register" className="text-blue-600 hover:underline">PLEKO</Link>, cada ingrediente de tu base de datos se vincula automaticamente a las recetas donde se usa.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">2</span>
              <div>
                <p className="font-semibold text-slate-800 mb-1">Aplica mermas reales, no estimaciones genericas</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Fijate como en el ejemplo del menu degustacion, el aguacate tiene un 30% de merma (hueso + piel) y la carrillera iberica un 25% (grasa, nervios). Estas mermas reales cambian drasticamente el coste. Un kilo de pulpo a 22 €/kg con 30% de merma cuesta realmente 31,43 €/kg utilizable. Pesa tus ingredientes antes y despues de la manipulacion para calcular la merma real de tu cocina. <Link to="/mermas-cocina" className="text-blue-600 hover:underline">Tabla completa de mermas</Link>.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">3</span>
              <div>
                <p className="font-semibold text-slate-800 mb-1">Actualiza los precios con cada factura</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Los precios que hemos usado en estos ejemplos son de junio 2026. En 3 meses, el aceite de oliva, el azafran o el atun rojo pueden haber variado un 15-20%. Un escandallo con precios de hace 6 meses es un escandallo que miente. La solucion ideal es un sistema que actualice automaticamente todos los escandallos cuando introduces un nuevo precio. Con el <Link to="/ocr-facturas-restaurante" className="text-blue-600 hover:underline">OCR de PLEKO</Link>, basta con hacer una foto al albaran.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">4</span>
              <div>
                <p className="font-semibold text-slate-800 mb-1">Diferencia entre coste total y coste por racion</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  En el ejemplo de la tarta de chocolate, el coste total es 14,76 € pero el coste por racion es solo 1,23 € (12 raciones). En el catering de bodas, el coste total es 2.720 € pero por racion son 27,20 €. Confundir estos dos valores es un error grave que puede llevar a fijar precios incorrectos. Siempre calcula y muestra ambos valores.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">5</span>
              <div>
                <p className="font-semibold text-slate-800 mb-1">Usa el escandallo para tomar decisiones de carta</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Con los 8 escandallos de esta guia, ya puedes ver que el gin tonic (17,8%) y el combo de cafeteria (20%) son los productos con mejor margen, mientras que el catering (32%) es el mas ajustado. La <Link to="/ingenieria-de-menu" className="text-blue-600 hover:underline">ingenieria de menu</Link> cruza el food cost de cada plato con su popularidad (unidades vendidas) para clasificarlos en Stars, Plowhorses, Puzzles y Dogs. Asi sabes que platos promocionar, cuales reformular y cuales eliminar.
                </p>
              </div>
            </div>
          </div>

          {/* Common errors */}
          <h2 id="errores-ejemplos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-red-500" />
              3 errores comunes que vemos en estos ejemplos
            </span>
          </h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-red-400 pl-5">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Error 1: No contabilizar el aceite de coccion</h3>
              <p className="text-slate-700 leading-relaxed">
                En la paella valenciana hemos incluido 60 ml de aceite de oliva virgen extra (0,51 €). Muchos restaurantes no lo incluyen porque "el aceite ya esta en la cocina". Pero con el aceite a 8,50 €/l en 2026, esos 60 ml por paella multiplicados por 40 paellas al dia son <strong>20,40 € diarios</strong> que nadie contabiliza. Al mes, son 612 € de coste invisible. Incluye siempre el aceite, la sal y cualquier ingrediente que uses, por poco que sea.
              </p>
            </div>
            <div className="border-l-4 border-red-400 pl-5">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Error 2: Aplicar la misma merma a todo</h3>
              <p className="text-slate-700 leading-relaxed">
                En el menu degustacion, hemos aplicado mermas que van del 0% (especias secas) al 30% (aguacate, pulpo). Algunos restaurantes usan un 10% generico para todo. Eso subestima el coste real de ingredientes con merma alta (el pulpo con 30% real vs. 10% estimado es un 29% de diferencia en el coste) y sobrestima el de ingredientes sin merma. Cada ingrediente necesita su merma real.
              </p>
            </div>
            <div className="border-l-4 border-red-400 pl-5">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Error 3: No recalcular al cambiar de proveedor o temporada</h3>
              <p className="text-slate-700 leading-relaxed">
                El tomate Raf del ejemplo de la hamburguesa esta a 5,90 €/kg (precio de temporada). Fuera de temporada, ese mismo tomate puede costar 12-15 €/kg. Si no recalculas el escandallo, tu hamburguesa gourmet pasa de un food cost del 28% a un 35% sin que nadie se entere. La solucion: <strong>revisa los escandallos cada vez que cambien los precios</strong> o, mejor aun, usa un <Link to="/software-escandallo" className="text-blue-600 hover:underline">software de escandallo</Link> que lo haga automaticamente.
              </p>
            </div>
          </div>

          {/* Mid-page CTA */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white mb-16">
            <TrendingUp className="w-10 h-10 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">¿Quieres calcular los escandallos de TODA tu carta?</h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el coste de todas tus recetas, controla mermas, escanea facturas con IA y te avisa cuando el food cost se dispara. Prueba gratis 14 dias, sin tarjeta de credito.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre ejemplos de escandallo</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cocteles' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/software-escandallo', label: 'El mejor software de escandallos' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food cost: que es y como calcularlo' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: tabla completa' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Calcula tus escandallos en minutos, no en horas</h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Estos 8 ejemplos te dan una base, pero tu restaurante necesita escandallos propios con tus precios, tus proveedores y tus mermas reales. PLEKO lo automatiza todo: escanea el albaran con el movil y todos los escandallos se actualizan al instante.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis — 14 dias sin tarjeta
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-blue-200 text-sm">Configurado en 2 minutos. Sin compromiso.</p>
          </div>
        </section>
      </div>
    </>
  );
}
